import { useCallback, useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { exportToCSV, downloadCSVTemplate } from '@/lib/utils/csvUtils'
import {
  CLIENT_CSV_HEADERS,
  CLIENT_CSV_TRASH_HEADERS,
  CLIENT_CSV_TEMPLATE_EXAMPLE_ROW,
  mapClientToCsvRow,
} from '@/lib/utils/clientCsv'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  ClientProvisioningModal,
  type ProvisioningPhase,
  type ProvisioningStep,
} from '@/components/forms/ClientProvisioningModal'
import { useClientContextStore, type ClientSummary } from '@/store/clientContext'
import { switchClient } from '@/lib/services/clientContextService'

type TabValue = 'all' | 'trash'

const defaultProvisioningSteps = (): ProvisioningStep[] => [
  { key: 'validate', label: 'Validating database name', status: 'pending' },
  { key: 'create_database', label: 'Creating database', status: 'pending' },
  { key: 'run_migrations', label: 'Running SIMA migrations', status: 'pending' },
  { key: 'seed_catalog_roots', label: 'Seeding asset catalog roots', status: 'pending' },
  { key: 'finalize', label: 'Finalizing', status: 'pending' },
]

/** Same rule as ClientForm: hide once provisioning status is completed. */
function clientNeedsDatabase(row: { database_provision_status?: string | null }): boolean {
  return String(row?.database_provision_status ?? '').toLowerCase() !== 'completed'
}

function clientHasDatabase(row: { database_provision_status?: string | null }): boolean {
  return !clientNeedsDatabase(row)
}

export default function ClientsPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)

  const [provisioningOpen, setProvisioningOpen] = useState(false)
  const [provisioningPhase, setProvisioningPhase] = useState<ProvisioningPhase>('provisioning_database')
  const [provisioningSteps, setProvisioningSteps] = useState<ProvisioningStep[]>(defaultProvisioningSteps())
  const [provisioningDatabaseName, setProvisioningDatabaseName] = useState<string | null>(null)
  const [provisioningError, setProvisioningError] = useState<string | null>(null)
  const pendingClientIdRef = useRef<number | null>(null)
  const pendingDatabaseNameRef = useRef<string | null>(null)

  const handleOpenAssetCatalog = useCallback(
    async (row: {
      id: number
      code?: string
      name?: string
      database_name?: string | null
    }) => {
      const code = String(row.code ?? '').trim()
      if (!code) {
        showWarning('This client has no code; cannot open Asset Catalog.')
        return
      }

      const fallback: ClientSummary = {
        id: Number(row.id),
        code,
        name: String(row.name ?? code),
        database_name: row.database_name ?? undefined,
      }

      try {
        const switched = await switchClient(code)
        setActiveClient(switched ?? fallback)
      } catch {
        setActiveClient(fallback)
      }

      navigate(`/organization/clients/${row.id}/asset-catalog/overview`)
    },
    [navigate, setActiveClient, showWarning]
  )

  const runProvisioning = useCallback(async (id: number, databaseName?: string | null) => {
    pendingClientIdRef.current = id
    pendingDatabaseNameRef.current = databaseName ?? null
    setProvisioningOpen(true)
    setProvisioningPhase('provisioning_database')
    setProvisioningSteps(defaultProvisioningSteps())
    setProvisioningDatabaseName(databaseName ?? null)
    setProvisioningError(null)

    try {
      const res = await apiClient.post(`/organization/clients/${id}/provision-database`)
      const result = res.data?.data ?? res.data
      const steps = (result?.steps ?? defaultProvisioningSteps()) as ProvisioningStep[]
      setProvisioningSteps(steps)
      setProvisioningDatabaseName(result?.database_name ?? databaseName ?? null)

      if (result?.status === 'completed') {
        setProvisioningPhase('done')
        showSuccess('Success', 'Client database provisioned successfully')
        tableRef.current?.reload()
        return
      }

      setProvisioningPhase('failed')
      setProvisioningError(result?.error ?? 'Database provisioning failed')
    } catch (error: any) {
      const result = error?.response?.data?.data ?? error?.response?.data
      if (result?.steps) {
        setProvisioningSteps(result.steps)
      }
      setProvisioningPhase('failed')
      setProvisioningError(result?.error ?? error?.message ?? 'Database provisioning failed')
    }
  }, [showSuccess])

  const handleCreateDatabase = useCallback(
    (row: { id: number; database_name?: string | null }) => {
      void runProvisioning(row.id, row.database_name)
    },
    [runProvisioning]
  )

  const handleProvisioningRetry = useCallback(() => {
    const id = pendingClientIdRef.current
    if (!id) return
    void runProvisioning(id, pendingDatabaseNameRef.current)
  }, [runProvisioning])

  const handleProvisioningClose = useCallback(() => {
    setProvisioningOpen(false)
  }, [])

  const statusFilterOptions = useMemo(
    () => [
      { value: '1', label: 'Active' },
      { value: '0', label: 'Inactive' },
    ],
    []
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        code: {
          name: 'Code',
          withSort: true,
          maxWidth: '120px',
        },
        domain: {
          name: 'Domain',
          withSort: true,
          maxWidth: '180px',
          render: (value: unknown) => {
            if (value === null || value === undefined || value === '') return '—'
            return String(value)
          },
        },
        name: {
          name: 'Name',
          withSort: true,
          maxWidth: '250px',
        },
        database_name: {
          name: 'Database',
          withSort: true,
          maxWidth: '150px',
        },
        purchase_orders_count: {
          name: 'POs',
          withSort: true,
          maxWidth: '80px',
        },
        status: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: {
            '1': 'green',
            '0': 'yellow',
            true: 'green',
            false: 'yellow',
          },
          badgeLabels: {
            '1': 'Active',
            '0': 'Inactive',
            true: 'Active',
            false: 'Inactive',
          },
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
          maxWidth: '150px',
        },
      },
      dataSource: activeTab === 'trash' ? '/organization/clients/trashed' : '/organization/clients',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No clients found',
      otherActions:
        activeTab === 'all'
          ? [
              ...(access.can_edit
                ? [
                    {
                      name: 'Create Database',
                      onClick: (row: { id: number; database_name?: string | null }) =>
                        handleCreateDatabase(row),
                      show: (row: { database_provision_status?: string | null }) =>
                        clientNeedsDatabase(row),
                    },
                  ]
                : []),
              {
                name: 'Manage Asset Catalog',
                onClick: (row: {
                  id: number
                  code?: string
                  name?: string
                  database_name?: string | null
                }) => handleOpenAssetCatalog(row),
                show: (row: { database_provision_status?: string | null }) => clientHasDatabase(row),
              },
            ]
          : undefined,
    }),
    [activeTab, statusFilterOptions, access.can_edit, handleCreateDatabase, handleOpenAssetCatalog]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these clients from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/organization/clients/bulk/restore'
        successMessage = 'Clients restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/organization/clients/bulk/force-delete'
            : '/organization/clients/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Clients permanently deleted successfully' : 'Clients moved to trash successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleDownloadTemplate = () => {
    downloadCSVTemplate({
      csvHeaders: [...CLIENT_CSV_HEADERS],
      exampleRow: CLIENT_CSV_TEMPLATE_EXAMPLE_ROW,
      filename: 'clients_template.csv',
      comments: [
        'Email Domains: domain;type;is_primary joined by | (types: corporate, ticketing_platform, msp_shared)',
        'Contact Emails: pipe-separated email addresses',
        'Database Name: leave blank to auto-generate on import',
      ],
      showError,
      showSuccess,
    })
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsImporting(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await apiClient.post<{ data: { imported: number; updated?: number; errors: string[] } }>(
        '/organization/clients/import',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      const d = data?.data ?? { imported: 0, updated: 0, errors: [] }
      const parts: string[] = []
      if (d.imported > 0) parts.push(`Created ${d.imported}`)
      if ((d.updated ?? 0) > 0) parts.push(`Updated ${d.updated}`)
      showSuccess('Success', parts.length > 0 ? `${parts.join(', ')} client(s)` : 'Import completed')
      if (d.errors?.length) {
        showWarning(d.errors.slice(0, 3).join('; ') + (d.errors.length > 3 ? '...' : ''))
      }
      tableRef.current?.reload()
    } catch (err: any) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: '/organization/clients',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: activeTab === 'trash' ? [...CLIENT_CSV_TRASH_HEADERS] : [...CLIENT_CSV_HEADERS],
        dataMapper: (client: any) => mapClientToCsvRow(client, activeTab === 'trash'),
        showError,
        showSuccess,
      })
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  const bulkActionOptions = useMemo(() => {
    const options = []
    if (activeTab === 'trash') {
      if (access.can_delete) {
        options.push({ value: 'restore', label: 'Restore' })
        options.push({ value: 'delete', label: 'Permanently Delete' })
      }
    } else {
      if (access.can_delete) {
        options.push({ value: 'delete', label: 'Move to Trash' })
      }
    }
    return options
  }, [activeTab, access])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Clients
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage client tenants and their configurations
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/clients"
                      onExport={handleExportData}
                      onDownloadTemplate={handleDownloadTemplate}
                      createLabel="Create New Client"
                      isExporting={isExporting}
                      isImporting={isImporting}
                      fileInputRef={fileInputRef}
                      onFileUpload={handleFileUpload}
                      acceptFileTypes=".csv"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by code, name..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/organization/clients/${id}`)}
                  filterOptions={{
                    status: statusFilterOptions,
                  }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <ClientProvisioningModal
        isOpen={provisioningOpen}
        phase={provisioningPhase}
        steps={provisioningSteps}
        databaseName={provisioningDatabaseName}
        error={provisioningError}
        onRetry={handleProvisioningRetry}
        onClose={handleProvisioningClose}
      />

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirmBulkAction}
              isLoading={isBulkProcessing}
              loadingText="Processing..."
            >
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
